import React, { useState, useEffect } from 'react';
import { Download, Save, Share2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBrand } from '../contexts/BrandContext';
import { usePricing } from '../hooks/usePricing';

interface PaymentMilestone {
  order: number;
  percentage: number;
  description: string;
}

interface InstallmentPlan {
  id: string;
  name: string;
  milestones: PaymentMilestone[];
  applicable_products: string[];
  applicable_brands: string[];
}

interface AdPlatform {
  id: string;
  name: string;
  price: number;
  additional_price?: number;
  tier_key: string;
}

interface Discount {
  id: string;
  code: string;
  type: string;
  value?: number;
  waive_setup: boolean;
  applies_to_services?: string[];
  min_spend?: number;
  max_discount?: number;
  base_value?: number;
  bonus_value?: number;
}

interface SummaryPanelProps {
  webTotal: number;
  webOriginalTotal: number;
  webDiscount: number;
  appliedWebDiscount: Discount | null;
  marketingMonthly: number;
  marketingSetup: number;
  marketingDiscount: number;
  marketingBreakdown: Record<string, number>;
  adSpend: number;
  includeWeb: boolean;
  includeMarketing: boolean;
  selectedAdPlatforms: string[];
  adPlatforms: AdPlatform[];
  clientType: string;
  discount: Discount | null;
  selectedProduct: string;
  seoTier: string | null;
  emailTier: string | null;
  croTier: string | null;
  selectedSupportPlan: string | null;
  multiLocation: number;
  customMarketingHours: number;
  onExportPDF: () => void;
  onSaveEstimate: () => void;
  onGenerateShareLink: () => void;
}

export default function SummaryPanel({
  webTotal,
  webOriginalTotal,
  webDiscount,
  appliedWebDiscount,
  marketingMonthly,
  marketingSetup,
  marketingDiscount,
  marketingBreakdown,
  adSpend,
  includeWeb,
  includeMarketing,
  selectedAdPlatforms,
  adPlatforms,
  clientType,
  discount,
  selectedProduct,
  seoTier,
  emailTier,
  croTier,
  selectedSupportPlan,
  multiLocation,
  customMarketingHours,
  onExportPDF,
  onSaveEstimate,
  onGenerateShareLink,
}: SummaryPanelProps) {
  const { selectedBrand } = useBrand();
  const { servicePricing, supportPlans, getServicesByCategory, getAdditionalPrice } = usePricing();
  const [installmentPlans, setInstallmentPlans] = useState<InstallmentPlan[]>([]);
  const [selectedInstallment, setSelectedInstallment] = useState<string | null>(null);

  const fmt = (n: number) => '$' + n.toLocaleString();

  useEffect(() => {
    if (selectedBrand) {
      loadInstallmentPlans();
    }
  }, [selectedBrand]);

  async function loadInstallmentPlans() {
    if (!selectedBrand) return;

    try {
      const { data, error } = await supabase
        .from('installment_plans')
        .select('*')
        .eq('brand_id', selectedBrand.id)
        .eq('active', true)
        .order('created_at');

      if (error) throw error;
      setInstallmentPlans(data || []);
    } catch (error) {
      console.error('Error loading installment plans:', error);
    }
  }

  const hasAds = selectedAdPlatforms.length > 0;
  const setupFeeOriginal = 500; // This should ideally come from pricing data
  const setupFeeWaived = discount && discount.waive_setup && hasAds;
  const setupFeeSavings = setupFeeWaived ? setupFeeOriginal : 0;

  const totalMonthlyInvestment = marketingMonthly + (includeMarketing ? adSpend : 0);
  const totalSetupFee = includeMarketing ? marketingSetup : 0;
  const totalDueNow = marketingMonthly + totalSetupFee;
  const totalSavings = marketingDiscount + setupFeeSavings;

  const calculateInstallments = () => {
    if (!selectedInstallment || !includeWeb) return null;

    const plan = installmentPlans.find(p => p.id === selectedInstallment);
    if (!plan || !plan.milestones || plan.milestones.length === 0) return null;

    const totalAmount = webTotal;
    const payments = plan.milestones.map(milestone => ({
      percentage: milestone.percentage,
      description: milestone.description,
      amount: totalAmount * (milestone.percentage / 100),
    }));

    return {
      plan,
      payments,
      totalAmount,
    };
  };

  const installmentInfo = calculateInstallments();
  const adjustedDueNow = installmentInfo
    ? installmentInfo.payments[0].amount + totalDueNow
    : webTotal + totalDueNow;

  const filteredInstallmentPlans = installmentPlans.filter(plan => {
    const productMatches = !plan.applicable_products || plan.applicable_products.length === 0 || plan.applicable_products.includes(selectedProduct);
    const brandMatches = !plan.applicable_brands || plan.applicable_brands.length === 0 || (selectedBrand && plan.applicable_brands.includes(selectedBrand.id));
    return productMatches && brandMatches;
  });

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm h-fit sticky top-6 min-w-[400px]">
      <h3 className="text-2xl font-bold mb-6">Summary</h3>

      {(includeMarketing || selectedSupportPlan) && (
        <div className="space-y-3 mb-6">
          <div className="pb-3">
            <h4 className="text-sm font-medium text-gray-600 mb-1">
              {includeMarketing ? 'Marketing Breakdown' : 'Website Care Plan'}
            </h4>
          </div>

          <div className="pb-3 border-b">
            <div className="space-y-2">
              {selectedSupportPlan && (() => {
                const plan = supportPlans.find(p => p.tier_key === selectedSupportPlan);
                if (!plan) return null;
                return (
                  <div key="support" className="flex justify-between items-baseline text-sm">
                    <span className="text-gray-600">{plan.name}</span>
                    <span className="font-semibold">{fmt(plan.price)}/mo</span>
                  </div>
                );
              })()}

              {includeMarketing && seoTier && (() => {
                const seo = getServicesByCategory('seo_services', clientType).find(s => s.tier_key === seoTier);
                if (!seo) return null;
                return (
                  <div key="seo" className="flex justify-between items-baseline text-sm">
                    <span className="text-gray-600">{seo.name}</span>
                    <span className="font-semibold">{fmt(seo.price)}/mo</span>
                  </div>
                );
              })()}

              {includeMarketing && emailTier && (() => {
                const email = getServicesByCategory('email_services', clientType).find(s => s.tier_key === emailTier);
                if (!email) return null;
                return (
                  <div key="email" className="flex justify-between items-baseline text-sm">
                    <span className="text-gray-600">{email.name}</span>
                    <span className="font-semibold">{fmt(email.price)}/mo</span>
                  </div>
                );
              })()}

              {includeMarketing && selectedAdPlatforms.map((platformKey, index) => {
                const platform = adPlatforms.find(p => p.tier_key === platformKey);
                if (!platform) return null;
                const price = index === 0 ? platform.price : (platform.additional_price || platform.price);
                return (
                  <div key={platformKey} className="flex justify-between items-baseline text-sm">
                    <span className="text-gray-600">{platform.name}</span>
                    <span className="font-semibold">{fmt(price)}/mo</span>
                  </div>
                );
              })}

              {includeMarketing && selectedAdPlatforms.length > 0 && (
                <div className="flex justify-between items-baseline text-sm">
                  <span className="text-gray-600">Ad Management (10%)</span>
                  <span className="font-semibold">{fmt(adSpend * 0.10)}/mo</span>
                </div>
              )}

              {includeMarketing && croTier && (() => {
                const cro = getServicesByCategory('cro_services').find(s => s.tier_key === croTier);
                if (!cro) return null;
                return (
                  <div key="cro" className="flex justify-between items-baseline text-sm">
                    <span className="text-gray-600">{cro.name}</span>
                    <span className="font-semibold">{fmt(cro.price)}/mo</span>
                  </div>
                );
              })()}

              {includeMarketing && multiLocation > 0 && (
                <div className="flex justify-between items-baseline text-sm">
                  <span className="text-gray-600">Multi-Location ({multiLocation})</span>
                  <span className="font-semibold">{fmt(multiLocation * getAdditionalPrice('multi_location'))}/mo</span>
                </div>
              )}

              {includeMarketing && customMarketingHours > 0 && (
                <div className="flex justify-between items-baseline text-sm">
                  <span className="text-gray-600">Custom Hours ({customMarketingHours})</span>
                  <span className="font-semibold">{fmt(customMarketingHours * getAdditionalPrice('hourly_rate'))}/mo</span>
                </div>
              )}
            </div>
          </div>

          <div className="pb-3 border-b">
            <div className="flex justify-between items-baseline">
              <span className="text-gray-700">Service Retainer</span>
              <span className="font-bold text-lg">{fmt(marketingMonthly)}/mo</span>
            </div>
          </div>

          <div className="pb-3 border-b">
            <div className="flex justify-between items-baseline">
              <span className="text-gray-700">Ad Spend Budget</span>
              <span className="font-bold text-lg">{fmt(adSpend)}/mo</span>
            </div>
          </div>

          <div className="pb-4 border-b">
            <div className="flex justify-between items-baseline">
              <span className="font-medium text-gray-900">Total Monthly Investment</span>
              <span className="font-bold text-xl text-green-600">{fmt(totalMonthlyInvestment)}/mo</span>
            </div>
          </div>

          <div className="pb-4 border-b">
            <div className="flex justify-between items-baseline">
              <span className="font-medium text-gray-700">Setup Fee</span>
              <span className="font-bold text-lg">
                {setupFeeWaived && (
                  <span className="line-through text-gray-400 mr-2">{fmt(setupFeeOriginal)}</span>
                )}
                {fmt(totalSetupFee)}
              </span>
            </div>
          </div>

          {(marketingDiscount > 0 || setupFeeWaived) && (
            <div className="pb-4 border-b bg-green-50 -mx-6 px-6 py-4">
              <h4 className="text-sm font-semibold text-green-800 mb-3">Savings Applied</h4>
              {marketingDiscount > 0 && (
                <div className="flex justify-between items-baseline text-sm mb-2">
                  <span className="text-green-700">Discount on Services</span>
                  <span className="font-semibold text-green-700">-{fmt(marketingDiscount)}/mo</span>
                </div>
              )}
              {setupFeeWaived && (
                <div className="flex justify-between items-baseline text-sm mb-2">
                  <span className="text-green-700">Setup Fee Waived</span>
                  <span className="font-semibold text-green-700">-{fmt(setupFeeSavings)}</span>
                </div>
              )}
              <div className="flex justify-between items-baseline pt-2 border-t border-green-200">
                <span className="font-semibold text-green-800">Total Savings</span>
                <span className="font-bold text-lg text-green-800">
                  {marketingDiscount > 0 && `-${fmt(marketingDiscount)}/mo`}
                  {marketingDiscount > 0 && setupFeeWaived && ' + '}
                  {setupFeeWaived && `-${fmt(setupFeeSavings)}`}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {includeWeb && filteredInstallmentPlans.length > 0 && (
        <div className="mb-6 pb-4 border-b">
          <h4 className="text-sm font-medium text-gray-600 mb-2">Payment Options</h4>
          <select
            value={selectedInstallment || ''}
            onChange={(e) => setSelectedInstallment(e.target.value || null)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gray-300 text-sm"
          >
            <option value="">Pay in Full</option>
            {filteredInstallmentPlans.map(plan => (
              <option key={plan.id} value={plan.id}>
                {plan.name} ({plan.milestones?.length || 0} payments)
              </option>
            ))}
          </select>
          {installmentInfo && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg text-xs space-y-2">
              <div className="font-semibold text-gray-800 mb-1">Payment Schedule:</div>
              {installmentInfo.payments.map((payment, index) => (
                <div key={index} className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium text-gray-700">
                      Payment {index + 1} ({payment.percentage}%)
                    </div>
                    <div className="text-xs text-gray-600">{payment.description}</div>
                  </div>
                  <span className="font-semibold ml-2">{fmt(payment.amount)}</span>
                </div>
              ))}
              <div className="pt-2 border-t border-gray-300 flex justify-between font-semibold">
                <span className="text-gray-700">Total:</span>
                <span>{fmt(installmentInfo.totalAmount)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="pt-4 border-t-2">
        <div className="mb-2">
          <span className="text-lg font-bold text-gray-900">Total Due Now</span>
        </div>
        <div className="text-4xl font-bold text-gray-900 mb-1">
          {fmt(adjustedDueNow)}
        </div>
        {installmentInfo && (
          <p className="text-xs text-gray-500 mt-1">
            {installmentInfo.numberOfSubsequent > 0 && `Plus ${installmentInfo.numberOfSubsequent} monthly payments of ${fmt(installmentInfo.subsequentPayments)}`}
          </p>
        )}
      </div>

      <div className="mt-6 space-y-2">
        <button
          onClick={onSaveEstimate}
          className="w-full py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 transition-colors"
        >
          <Save className="w-4 h-4" />
          Save Estimate
        </button>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onExportPDF}
            className="py-2 px-4 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center justify-center gap-2 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export PDF
          </button>
          <button
            onClick={onGenerateShareLink}
            className="py-2 px-4 bg-gray-700 text-white rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2 transition-colors"
          >
            <Share2 className="w-4 h-4" />
            Share Link
          </button>
        </div>
      </div>
    </div>
  );
}
